import { find, get } from 'lodash';
import { CALL_VIA_PHONE_NUMBER } from '../../../../../panels/voip-test-now-panels/VoipTestConstants';
import {
    AGENT_ALL_FIELD_NAME,
    AGENT_IDS_FIELD_NAME,
    TESTCONFIG_CALLEE_FIELD_NAME,
    TESTCONFIG_CALLER_FIELD_NAME,
    TESTCONFIG_CALLVIA_FIELD_NAME,
    VOIP_IP_VERSION_IPV4,
    VOIP_IP_VERSION_IPV6,
} from '../../../ServiceTestsConstants';
import { SHOULD_DIRTY_TRUE } from '../../../ServiceTestsFormControlConstants';

/**
 * Build the testConfig party object (caller or callee) from a selected agent.
 */
export function buildPartyConfig(agent) {
    const base = {
        ids: [{ id: agent.id, guid: agent.guid }],
        sipUser: get(agent, 'voipConfiguration.sipUserId', null),
        extension: get(agent, 'voipConfiguration.extension', null),
        ipVersion: get(agent, 'voipConfiguration.ipVersion', VOIP_IP_VERSION_IPV4),
    };

    // For call-manager agents with resolved manager, include PBX routing fields
    if (!agent.useDirectCalling && agent.callManagerResolved) {
        const cm = get(agent, 'voipConfiguration.primaryCallManager', {});
        base.domain = cm.domain;
        base.sipProxy = cm.sipProxy;
        base.sipPort = cm.sipPort;
        base.rtpStartPort = cm.rtpStartPort;
        base.rtpEndPort = cm.rtpEndPort;
    }

    return base;
}

/**
 * Build callVia config when caller uses direct calling.
 * Replicates portal/ui updateDirectCallingSipUrl logic for Pulse-to-Pulse.
 */
export function buildDirectCallingCallVia(calleeAgent) {
    const phoneNumber = get(calleeAgent, 'voipConfiguration.phoneNumber', '');
    const callVia = {
        head: CALL_VIA_PHONE_NUMBER,
        body: phoneNumber,
        useDirectCalling: true,
    };

    // Append callee IP to create full SIP URI
    const interfaces = get(calleeAgent, 'network.reportedInterfaces.interfaces', []);
    const ipVersion = get(calleeAgent, 'voipConfiguration.ipVersion', VOIP_IP_VERSION_IPV4);
    const voipInterface = find(interfaces, (itf) => get(itf, 'is_selected_voip'));

    if (voipInterface) {
        const rawIp = ipVersion === VOIP_IP_VERSION_IPV6 ? voipInterface.ipv6 : voipInterface.ipv4;
        if (rawIp) {
            const ip = ipVersion === VOIP_IP_VERSION_IPV6 ? `[${rawIp.split('/')[0]}]` : rawIp.split('/')[0];
            if (ip && ip !== '0.0.0.0' && ip !== '[::]') {
                callVia.body = `${phoneNumber}@${ip}`;
            }
        }
    }

    return callVia;
}

/**
 * Build callVia config for non-direct-calling (call-manager) mode.
 * Preserves existing callVia from saved test data when available.
 */
export function buildCallManagerCallVia(calleeAgent, existingCallVia) {
    // Preserve existing callVia head/body if present (user may have configured extension vs phone)
    if (existingCallVia?.head) {
        return {
            head: existingCallVia.head,
            body: existingCallVia.body || get(calleeAgent, 'voipConfiguration.phoneNumber', ''),
            useDirectCalling: false,
        };
    }
    // Default to phone number
    return {
        head: CALL_VIA_PHONE_NUMBER,
        body: get(calleeAgent, 'voipConfiguration.phoneNumber', ''),
        useDirectCalling: false,
    };
}

/**
 * Update all form fields after both caller and callee are known.
 */
export function applySelectionToForm(callerAgent, calleeAgent, setValue, existingCallVia) {
    setValue(TESTCONFIG_CALLER_FIELD_NAME, buildPartyConfig(callerAgent), SHOULD_DIRTY_TRUE);
    setValue(TESTCONFIG_CALLEE_FIELD_NAME, buildPartyConfig(calleeAgent), SHOULD_DIRTY_TRUE);

    // Build callVia based on direct-calling mode
    // Both agents must match mode (enforced by callee filter), so check either
    if (callerAgent.useDirectCalling) {
        setValue(TESTCONFIG_CALLVIA_FIELD_NAME, buildDirectCallingCallVia(calleeAgent), SHOULD_DIRTY_TRUE);
    } else {
        setValue(
            TESTCONFIG_CALLVIA_FIELD_NAME,
            buildCallManagerCallVia(calleeAgent, existingCallVia),
            SHOULD_DIRTY_TRUE
        );
    }

    // agents.ids is a flat array of agent ID strings
    setValue(AGENT_IDS_FIELD_NAME, [callerAgent.id, calleeAgent.id], SHOULD_DIRTY_TRUE);
    setValue(AGENT_ALL_FIELD_NAME, false, SHOULD_DIRTY_TRUE);
}

/**
 * Clear a party's form fields.
 */
export function clearPartyFromForm(party, setValue) {
    setValue(`testConfig.${party}`, { ids: [] }, SHOULD_DIRTY_TRUE);
}
